import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAuthHeaders } from '../utils/api';
import MobileModal from './MobileModal';
import TrainingReports from './TrainingReports';
import { PrognosticTableCells, PROGNOSTIC_HEADERS } from './PrognosticMetrics';

// Code-split: keeps Recharts out of this page's initial load.
const CoachAnalytics = lazy(() => import('./analytics/CoachAnalytics'));

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const parseIntervalDetails = (intervalDetails) => {
  if (!intervalDetails) return [];
  if (Array.isArray(intervalDetails)) return intervalDetails;
  if (typeof intervalDetails === 'string') {
    try {
      const parsed = JSON.parse(intervalDetails);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

export default function InternalReports({ token }) {
  const { isCoach, isAdmin, isSuperAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('training');

  // Sign-up Reports state
  const [sessionReport, setSessionReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState('');
  const [reportTeamFilter, setReportTeamFilter] = useState('');
  const [reportClubFilter, setReportClubFilter] = useState('');
  const [availableClubs, setAvailableClubs] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [selectedAthleteHistory, setSelectedAthleteHistory] = useState(null);

  // Template Reports state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateReport, setTemplateReport] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [expandedTemplateRows, setExpandedTemplateRows] = useState({});

  // Equipment/Reservation Reports state
  const [reservationReport, setReservationReport] = useState(null);
  const [resReportLoading, setResReportLoading] = useState(false);
  const [resReportStartDate, setResReportStartDate] = useState('');
  const [resReportEndDate, setResReportEndDate] = useState('');
  const [selectedBoatHistory, setSelectedBoatHistory] = useState(null);
  // Meters Report: unified totals from user_meters_log (no double count)
  const [unifiedMetersReport, setUnifiedMetersReport] = useState(null);
  const [unifiedMetersLoading, setUnifiedMetersLoading] = useState(false);
  const [metersReportStartDate, setMetersReportStartDate] = useState('');
  const [metersReportEndDate, setMetersReportEndDate] = useState('');
  const [metersTeamFilter, setMetersTeamFilter] = useState('');
  const [unifiedMetersDetail, setUnifiedMetersDetail] = useState(null);

  // Damage Reports state
  const [damageReports, setDamageReports] = useState([]);
  const [damageLoading, setDamageLoading] = useState(false);
  const [damageStatusFilter, setDamageStatusFilter] = useState('all');
  const [damageSeverityFilter, setDamageSeverityFilter] = useState('all');

  useEffect(() => {
    fetchTeams();
    if (isSuperAdmin()) {
      fetchAvailableClubs();
    }
  }, []);

  // Fetch available teams
  const fetchTeams = async () => {
    try {
      const response = await fetch(`${API_URL}/api/athletes`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        const teams = [...new Set(data.filter(a => a.team).map(a => a.team))].sort();
        setAvailableTeams(teams);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  // Fetch available clubs for superadmins
  const fetchAvailableClubs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teams`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableClubs(data.teams?.map(t => t.club_code) || []);
      }
    } catch (error) {
      console.error('Error fetching clubs:', error);
    }
  };

  // ==================== TRAINING REPORTS ====================

  const fetchSessionReport = async () => {
    if (!reportStartDate || !reportEndDate) {
      alert('Please select both start and end dates');
      return;
    }
    setReportLoading(true);
    try {
      const startFormatted = new Date(reportStartDate).toLocaleDateString('en-US');
      const endFormatted = new Date(reportEndDate).toLocaleDateString('en-US');
      let url = `${API_URL}/api/workouts/signups/attendance-report?startDate=${startFormatted}&endDate=${endFormatted}`;
      if (reportTeamFilter) {
        url += `&team=${encodeURIComponent(reportTeamFilter)}`;
      }
      if (isSuperAdmin() && reportClubFilter) {
        url += `&club=${encodeURIComponent(reportClubFilter)}`;
      }
      
      const response = await fetch(url, { headers: getAuthHeaders(token) });
      if (response.ok) {
        const data = await response.json();
        setSessionReport(data);
      } else {
        alert('Failed to generate report');
      }
    } catch (error) {
      console.error('Error fetching session report:', error);
      alert('Error generating report. Please try again.');
    } finally {
      setReportLoading(false);
    }
  };

  const fetchAthleteHistory = async (athleteId) => {
    try {
      const startFormatted = reportStartDate ? new Date(reportStartDate).toLocaleDateString('en-US') : '';
      const endFormatted = reportEndDate ? new Date(reportEndDate).toLocaleDateString('en-US') : '';
      let url = `${API_URL}/api/workouts/signups/athlete/${athleteId}/history`;
      if (startFormatted && endFormatted) {
        url += `?startDate=${startFormatted}&endDate=${endFormatted}`;
      }
      const response = await fetch(url, { headers: getAuthHeaders(token) });
      if (response.ok) {
        const data = await response.json();
        setSelectedAthleteHistory(data);
      }
    } catch (error) {
      console.error('Error fetching athlete history:', error);
    }
  };

  const exportSessionReportCSV = () => {
    if (!sessionReport) return;
    const showClub = isSuperAdmin() && !reportClubFilter;
    const headers = showClub 
      ? ['Name', 'Club', 'Team', 'Email', 'Sign-ups', 'Unique Days', 'First Session', 'Last Session']
      : ['Name', 'Team', 'Email', 'Sign-ups', 'Unique Days', 'First Session', 'Last Session'];
    const rows = sessionReport.report.map(r => {
      const baseRow = [
        r.athlete_name,
        r.athlete_team || '',
        r.user_email || '',
        r.signup_count,
        r.unique_days,
        r.first_signup ? new Date(r.first_signup).toLocaleDateString() : '',
        r.last_signup ? new Date(r.last_signup).toLocaleDateString() : ''
      ];
      if (showClub) {
        baseRow.splice(1, 0, r.athlete_club || '');
      }
      return baseRow;
    });
    
    const csvContent = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `training-signups-${reportStartDate}-to-${reportEndDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // ==================== TEMPLATE REPORTS ====================

  const fetchTemplates = async () => {
    setTemplateLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/result-templates`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        // Backend returns array directly, not wrapped in { templates: [] }
        setTemplates(Array.isArray(data) ? data : (data.templates || []));
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setTemplateLoading(false);
    }
  };

  const fetchTemplateReport = async (templateId) => {
    setTemplateLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/result-templates/${templateId}/report`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setTemplateReport(data);
        setSelectedTemplate(templateId);
      }
    } catch (error) {
      console.error('Error fetching template report:', error);
    } finally {
      setTemplateLoading(false);
    }
  };

  const formatTime = (totalSeconds) => {
    if (!totalSeconds) return '-';
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const tenths = Math.floor((totalSeconds % 1) * 10);
    return `${minutes}:${seconds.toString().padStart(2, '0')}.${tenths}`;
  };

  // ==================== EQUIPMENT REPORTS ====================

  const fetchBoatUsageReport = async () => {
    if (!resReportStartDate || !resReportEndDate) {
      alert('Please select both start and end dates');
      return;
    }
    setResReportLoading(true);
    try {
      const startFormatted = resReportStartDate;
      const endFormatted = resReportEndDate;
      const response = await fetch(
        `${API_URL}/api/boats/meters/mileage-report?startDate=${startFormatted}&endDate=${endFormatted}`,
        { headers: getAuthHeaders(token) }
      );
      if (response.ok) {
        const data = await response.json();
        setReservationReport(data);
      } else {
        alert('Failed to generate equipment mileage report');
      }
    } catch (error) {
      console.error('Error fetching equipment mileage report:', error);
      alert('Failed to generate equipment mileage report');
    } finally {
      setResReportLoading(false);
    }
  };

  const fetchUnifiedMetersReport = async () => {
    if (!metersReportStartDate || !metersReportEndDate) {
      alert('Please select both start and end dates');
      return;
    }
    setUnifiedMetersLoading(true);
    try {
      let url = `${API_URL}/api/boats/meters/unified-report?startDate=${encodeURIComponent(
        metersReportStartDate
      )}&endDate=${encodeURIComponent(metersReportEndDate)}`;
      if (isAdmin() && metersTeamFilter) {
        url += `&team=${encodeURIComponent(metersTeamFilter)}`;
      }
      const response = await fetch(url, { headers: getAuthHeaders(token) });
      if (response.ok) {
        setUnifiedMetersReport(await response.json());
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.error || 'Failed to generate Meters report');
      }
    } catch (error) {
      console.error('Error fetching unified meters report:', error);
      alert('Failed to generate Meters report');
    } finally {
      setUnifiedMetersLoading(false);
    }
  };

  const exportUnifiedMetersCSV = () => {
    if (!unifiedMetersReport?.report) return;
    const headers = ['Name', 'Team', 'On-water (m)', 'Training (m)', 'Other (m)', 'Total (m)', 'Log count'];
    const rows = unifiedMetersReport.report.map((r) => [
      r.user_name,
      r.user_team || '',
      parseInt(r.water_meters, 10) || 0,
      parseInt(r.training_meters, 10) || 0,
      parseInt(r.other_meters, 10) || 0,
      parseInt(r.total_meters, 10) || 0,
      r.log_count
    ]);
    const csvContent = [headers.join(','), ...rows.map((row) => row.map((c) => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `meters-report-${metersReportStartDate}-to-${metersReportEndDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const openUnifiedMetersDetail = async (row) => {
    setUnifiedMetersDetail({ user: row, lines: [], loading: true });
    try {
      const detailUrl = `${API_URL}/api/boats/meters/unified-report/athlete/${row.user_id}?startDate=${encodeURIComponent(
        metersReportStartDate
      )}&endDate=${encodeURIComponent(metersReportEndDate)}`;
      const res = await fetch(detailUrl, { headers: getAuthHeaders(token) });
      if (!res.ok) {
        setUnifiedMetersDetail({
          user: row,
          lines: [],
          loading: false,
          error: 'Could not load details'
        });
        return;
      }
      const data = await res.json();
      setUnifiedMetersDetail({ user: data.user, lines: data.lines, loading: false });
    } catch (e) {
      console.error(e);
      setUnifiedMetersDetail({
        user: row,
        lines: [],
        loading: false,
        error: 'Could not load details'
      });
    }
  };

  const exportBoatUsageCSV = () => {
    if (!reservationReport) return;
    const headers = ['Boat Name', 'Class', 'Type', 'Meter Logs', 'Unique Loggers', 'Unique Days', 'Total Meters', 'First Log', 'Last Log'];
    const rows = reservationReport.report.map(r => [
      r.boat_name,
      r.boat_class,
      r.ownership_type,
      r.log_count,
      r.unique_loggers,
      r.unique_days,
      parseInt(r.total_meters || 0, 10),
      r.first_log_date ? new Date(r.first_log_date).toLocaleDateString() : '',
      r.last_log_date ? new Date(r.last_log_date).toLocaleDateString() : ''
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `equipment-mileage-${resReportStartDate}-to-${resReportEndDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // ==================== DAMAGE REPORTS ====================

  const fetchDamageReports = async () => {
    setDamageLoading(true);
    try {
      let url = `${API_URL}/api/boats/damage-reports`;
      const params = new URLSearchParams();
      if (damageStatusFilter !== 'all') params.append('status', damageStatusFilter);
      if (damageSeverityFilter !== 'all') params.append('severity', damageSeverityFilter);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, { headers: getAuthHeaders(token) });
      if (response.ok) {
        const data = await response.json();
        setDamageReports(data.reports || []);
      }
    } catch (error) {
      console.error('Error fetching damage reports:', error);
    } finally {
      setDamageLoading(false);
    }
  };

  const exportDamageCSV = () => {
    if (!damageReports.length) return;
    const headers = ['Boat', 'Class', 'Severity', 'Status', 'Location', 'Description', 'Reported By', 'Assigned To', 'Date', 'Resolution'];
    const rows = damageReports.map(r => [
      r.boat_name,
      r.boat_class,
      r.severity,
      r.status,
      r.location_on_boat || '',
      r.description,
      r.reported_by_name,
      r.assignee_name || '',
      r.discovered_date,
      r.resolution_notes || ''
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `damage-reports-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'minor': return 'bg-yellow-100 text-yellow-800';
      case 'moderate': return 'bg-orange-100 text-orange-800';
      case 'severe': return 'bg-red-100 text-red-800';
      case 'critical': return 'bg-red-600 text-white';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-red-100 text-red-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isCoach() && !isAdmin()) {
    return (
      <div className="p-6 text-center text-gray-500">
        You don't have permission to view internal reports.
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl sm:text-3xl font-bold text-theme-primary mb-6">Internal Reports</h1>

      {/* Tab Navigation */}
      <div className="flex gap-1 sm:gap-2 mb-6 border-b overflow-x-auto pb-1">
        <button
          onClick={() => setActiveTab('training')}
          className={`pb-2 px-3 sm:px-4 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
            activeTab === 'training'
              ? 'border-b-2 border-theme-primary text-theme-primary'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Training Reports
        </button>
        <button
          onClick={() => setActiveTab('signups')}
          className={`pb-2 px-3 sm:px-4 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
            activeTab === 'signups'
              ? 'border-b-2 border-theme-primary text-theme-primary'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Sign-Up Reports
        </button>
        <button
          onClick={() => {
            setActiveTab('templates');
            if (templates.length === 0) fetchTemplates();
          }}
          className={`pb-2 px-3 sm:px-4 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
            activeTab === 'templates'
              ? 'border-b-2 border-theme-primary text-theme-primary'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Template Reports
        </button>
        <button
          onClick={() => setActiveTab('equipment')}
          className={`pb-2 px-3 sm:px-4 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
            activeTab === 'equipment'
              ? 'border-b-2 border-theme-primary text-theme-primary'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Equipment Reports
        </button>
        <button
          onClick={() => setActiveTab('meters')}
          className={`pb-2 px-3 sm:px-4 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
            activeTab === 'meters'
              ? 'border-b-2 border-theme-primary text-theme-primary'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Meters Report
        </button>
        <button
          onClick={() => {
            setActiveTab('damage');
            if (damageReports.length === 0) fetchDamageReports();
          }}
          className={`pb-2 px-3 sm:px-4 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
            activeTab === 'damage'
              ? 'border-b-2 border-theme-primary text-theme-primary'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Damage Reports
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          className={`pb-2 px-3 sm:px-4 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
            activeTab === 'analytics'
              ? 'border-b-2 border-theme-primary text-theme-primary'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Analytics
        </button>
      </div>

      {/* ==================== TRAINING REPORTS TAB ==================== */}
      {activeTab === 'training' && (
        <TrainingReports />
      )}

      {/* ==================== TEAM ANALYTICS TAB ==================== */}
      {activeTab === 'analytics' && (
        <Suspense fallback={<div className="flex items-center justify-center h-64 text-theme-primary">Loading analytics...</div>}>
          <CoachAnalytics embedded />
        </Suspense>
      )}

      {/* ==================== SIGN-UP REPORTS TAB ==================== */}
      {activeTab === 'signups' && (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-theme-primary mb-2">Sign-Up Session Report</h3>
            <p className="text-sm text-gray-600 mb-4">
              Generate a report of workout sign-ups for invoicing purposes.
            </p>
            <div className={`grid grid-cols-1 gap-3 ${isSuperAdmin() ? 'sm:grid-cols-5' : 'sm:grid-cols-4'}`}>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Start Date</label>
                <input
                  type="date"
                  value={reportStartDate}
                  onChange={(e) => setReportStartDate(e.target.value)}
                  className="border rounded px-3 py-2 text-sm w-full"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">End Date</label>
                <input
                  type="date"
                  value={reportEndDate}
                  onChange={(e) => setReportEndDate(e.target.value)}
                  className="border rounded px-3 py-2 text-sm w-full"
                />
              </div>
              {isSuperAdmin() && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Club</label>
                  <select
                    value={reportClubFilter}
                    onChange={(e) => setReportClubFilter(e.target.value)}
                    className="border rounded px-3 py-2 text-sm w-full"
                  >
                    <option value="">All Clubs</option>
                    {availableClubs.map((club) => (
                      <option key={club} value={club}>{club}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs text-gray-600 mb-1">Team</label>
                <select
                  value={reportTeamFilter}
                  onChange={(e) => setReportTeamFilter(e.target.value)}
                  className="border rounded px-3 py-2 text-sm w-full"
                >
                  <option value="">All Teams</option>
                  {availableTeams.map((team) => (
                    <option key={team} value={team}>{team}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={fetchSessionReport}
                  disabled={!reportStartDate || !reportEndDate || reportLoading}
                  className="bg-theme-primary text-white px-4 py-2 rounded text-sm hover:opacity-90 disabled:opacity-50 w-full"
                >
                  {reportLoading ? 'Generating...' : 'Generate'}
                </button>
              </div>
            </div>
          </div>

          {sessionReport && (
            <div className="space-y-4">
              <div className="bg-theme-bg-secondary rounded-lg p-4">
                <h3 className="font-semibold text-theme-primary mb-3">Summary</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-theme-primary">{sessionReport.summary.total_athletes}</div>
                    <div className="text-xs text-gray-600">Athletes</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-theme-primary">{sessionReport.summary.total_signups}</div>
                    <div className="text-xs text-gray-600">Total Sign-ups</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-theme-primary">{sessionReport.summary.total_workouts}</div>
                    <div className="text-xs text-gray-600">Sessions</div>
                  </div>
                  <div>
                    <button
                      onClick={exportSessionReportCSV}
                      className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700"
                    >
                      Export CSV
                    </button>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full bg-white rounded-lg shadow-sm text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 font-semibold">Athlete</th>
                      {isSuperAdmin() && !reportClubFilter && (
                        <th className="text-center p-3 font-semibold">Club</th>
                      )}
                      <th className="text-center p-3 font-semibold">Team</th>
                      <th className="text-center p-3 font-semibold">Sign-ups</th>
                      <th className="text-center p-3 font-semibold">Days</th>
                      <th className="text-center p-3 font-semibold">First</th>
                      <th className="text-center p-3 font-semibold">Last</th>
                      <th className="text-center p-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessionReport.report.map((row) => (
                      <tr key={row.athlete_id} className="border-t hover:bg-gray-50">
                        <td className="p-3">
                          <div className="font-medium">{row.athlete_name}</div>
                          {row.user_email && (
                            <div className="text-xs text-gray-500">{row.user_email}</div>
                          )}
                        </td>
                        {isSuperAdmin() && !reportClubFilter && (
                          <td className="p-3 text-center text-gray-600 text-xs">{row.athlete_club || '-'}</td>
                        )}
                        <td className="p-3 text-center text-gray-600">{row.athlete_team || '-'}</td>
                        <td className="p-3 text-center font-mono font-semibold">{row.signup_count}</td>
                        <td className="p-3 text-center font-mono">{row.unique_days}</td>
                        <td className="p-3 text-center text-xs">
                          {row.first_signup ? new Date(row.first_signup).toLocaleDateString() : '-'}
                        </td>
                        <td className="p-3 text-center text-xs">
                          {row.last_signup ? new Date(row.last_signup).toLocaleDateString() : '-'}
                        </td>
                        <td className="p-3 text-center">
                          <button
                            onClick={() => fetchAthleteHistory(row.athlete_id)}
                            className="text-theme-primary hover:underline text-sm"
                          >
                            Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {sessionReport.report.length === 0 && (
                <p className="text-gray-500 text-center py-4">No sign-ups found for the selected period.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ==================== TEMPLATE REPORTS TAB ==================== */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-theme-primary mb-2">Result Template Reports</h3>
            <p className="text-sm text-gray-600 mb-4">
              View submissions for result templates you've created.
            </p>
            
            {templateLoading && !templateReport ? (
              <div className="text-center py-4 text-gray-500">Loading templates...</div>
            ) : templates.length === 0 ? (
              <p className="text-gray-500">No templates found. Create templates in the Results page.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {templates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => fetchTemplateReport(template.id)}
                    className={`p-4 rounded-lg border text-left transition-colors ${
                      selectedTemplate === template.id
                        ? 'border-theme-primary bg-theme-bg-secondary'
                        : 'border-gray-200 hover:border-theme-primary hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-semibold text-theme-primary">{template.name}</div>
                    <div className="text-sm text-gray-600">
                      {template.piece_type === 'time' 
                        ? `${Math.floor((template.time_seconds || 0) / 60)}' piece`
                        : `${template.distance_meters}m`
                      }
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Due: {new Date(template.due_date).toLocaleDateString()}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {templateReport && (
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-theme-primary">{templateReport.template.name} - Submissions</h3>
                <span className="text-sm text-gray-500">
                  {templateReport.submissions.length} submission(s)
                </span>
              </div>
              {(templateReport.template.wind_speed_kts !== null ||
                templateReport.template.wind_direction ||
                templateReport.template.flow_rate !== null ||
                templateReport.template.water_temperature_c !== null ||
                templateReport.template.time_of_day) && (
                <p className="text-sm text-teal-700 mb-3">
                  Conditions: {[
                    templateReport.template.wind_speed_kts !== null && templateReport.template.wind_speed_kts !== undefined ? `Wind ${templateReport.template.wind_speed_kts} kts` : null,
                    templateReport.template.wind_direction ? `Direction ${templateReport.template.wind_direction}` : null,
                    templateReport.template.flow_rate !== null && templateReport.template.flow_rate !== undefined ? `Flow ${templateReport.template.flow_rate} cfs` : null,
                    templateReport.template.water_temperature_c !== null && templateReport.template.water_temperature_c !== undefined ? `Water ${templateReport.template.water_temperature_c}°C` : null,
                    templateReport.template.time_of_day ? `Time ${templateReport.template.time_of_day}` : null
                  ].filter(Boolean).join(' • ')}
                </p>
              )}

              {templateReport.submissions.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No submissions yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-3 font-semibold">Athlete</th>
                        <th className="text-center p-3 font-semibold">Date</th>
                        <th className="text-center p-3 font-semibold">Time</th>
                        <th className="text-center p-3 font-semibold">Avg Split</th>
                        <th className="text-center p-3 font-semibold">CST %</th>
                        {PROGNOSTIC_HEADERS.map((h) => (
                          <th key={h.key} className="text-right p-3 font-semibold">{h.label}</th>
                        ))}
                        <th className="text-center p-3 font-semibold">Pieces</th>
                      </tr>
                    </thead>
                    <tbody>
                      {templateReport.submissions.map((sub) => {
                        const pieces = parseIntervalDetails(sub.interval_details);
                        const isExpanded = !!expandedTemplateRows[sub.id];
                        return (
                          <React.Fragment key={sub.id}>
                            <tr className="border-t hover:bg-gray-50">
                              <td className="p-3 font-medium">{sub.athlete_name}</td>
                              <td className="p-3 text-center">{sub.result_date}</td>
                              <td className="p-3 text-center font-mono">{formatTime(sub.total_time_seconds)}</td>
                              <td className="p-3 text-center font-mono">{formatTime(sub.avg_split_seconds)}</td>
                              <td className="p-3 text-center font-mono">
                                {sub.gms_percentage != null
                                  ? `${sub.gms_percentage.toFixed(1)}%`
                                  : '—'}
                              </td>
                              <PrognosticTableCells result={sub} />
                              <td className="p-3 text-center">
                                {pieces.length > 0 ? (
                                  <button
                                    type="button"
                                    onClick={() => setExpandedTemplateRows(prev => ({ ...prev, [sub.id]: !prev[sub.id] }))}
                                    className="text-xs px-2 py-1 rounded border border-blue-200 text-blue-700 hover:bg-blue-50"
                                  >
                                    {isExpanded ? 'Hide' : `Show ${pieces.length}`}
                                  </button>
                                ) : (
                                  <span className="text-xs text-gray-400">-</span>
                                )}
                              </td>
                            </tr>
                            {isExpanded && pieces.length > 0 && (
                              <tr className="bg-gray-50 border-t">
                                <td colSpan={10} className="p-3">
                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                                    {pieces.map((piece, idx) => (
                                      <div key={`${sub.id}-${idx}`} className="rounded border bg-white px-3 py-2 text-xs text-left">
                                        <p className="font-semibold text-gray-700 mb-1">Piece {idx + 1}</p>
                                        <p>Time: {piece.time_seconds ? formatTime(piece.time_seconds) : '-'}</p>
                                        <p>Distance: {piece.distance_meters || '-'}m</p>
                                        <p>SR: {piece.stroke_rate || '-'}</p>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ==================== EQUIPMENT REPORTS TAB ==================== */}
      {activeTab === 'equipment' && (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-theme-primary mb-2">Equipment Mileage Report</h3>
            <p className="text-sm text-gray-600 mb-4">
              Generate a report of meters logged by boat/equipment.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Start Date</label>
                <input
                  type="date"
                  value={resReportStartDate}
                  onChange={(e) => setResReportStartDate(e.target.value)}
                  className="border rounded px-3 py-2 text-sm w-full"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">End Date</label>
                <input
                  type="date"
                  value={resReportEndDate}
                  onChange={(e) => setResReportEndDate(e.target.value)}
                  className="border rounded px-3 py-2 text-sm w-full"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={fetchBoatUsageReport}
                  disabled={!resReportStartDate || !resReportEndDate || resReportLoading}
                  className="bg-theme-primary text-white px-4 py-2 rounded text-sm hover:opacity-90 disabled:opacity-50 w-full"
                >
                  {resReportLoading ? 'Generating...' : 'Generate'}
                </button>
              </div>
            </div>
          </div>

          {reservationReport && (
            <div className="space-y-4">
              <div className="bg-theme-bg-secondary rounded-lg p-4">
                <h3 className="font-semibold text-theme-primary mb-3">Summary</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-theme-primary">{reservationReport.summary.total_boats}</div>
                    <div className="text-xs text-gray-600">Boats Logged</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-theme-primary">{reservationReport.summary.total_logs}</div>
                    <div className="text-xs text-gray-600">Meter Logs</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-theme-primary">{Number(reservationReport.summary.total_meters || 0).toLocaleString()}</div>
                    <div className="text-xs text-gray-600">Total Meters</div>
                  </div>
                  <div>
                    <button
                      onClick={exportBoatUsageCSV}
                      className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700"
                    >
                      Export CSV
                    </button>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full bg-white rounded-lg shadow-sm text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 font-semibold">Boat</th>
                      <th className="text-center p-3 font-semibold">Meter Logs</th>
                      <th className="text-center p-3 font-semibold">Loggers</th>
                      <th className="text-center p-3 font-semibold">Days</th>
                      <th className="text-center p-3 font-semibold">Meters</th>
                      <th className="text-center p-3 font-semibold">First</th>
                      <th className="text-center p-3 font-semibold">Last</th>
                      <th className="text-center p-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reservationReport.report.map((row) => (
                      <tr key={row.boat_id} className="border-t hover:bg-gray-50">
                        <td className="p-3">
                          <div className="font-medium">{row.boat_name}</div>
                          <div className="text-xs text-gray-500">{row.boat_class}</div>
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {row.ownership_type === 'private' && (
                              <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Private</span>
                            )}
                            {row.is_retired && (
                              <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">Retired</span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-center font-mono font-semibold">{row.log_count}</td>
                        <td className="p-3 text-center font-mono">{row.unique_loggers}</td>
                        <td className="p-3 text-center font-mono">{row.unique_days}</td>
                        <td className="p-3 text-center font-mono">{Number(row.total_meters || 0).toLocaleString()}</td>
                        <td className="p-3 text-center text-xs">
                          {row.first_log_date ? new Date(row.first_log_date).toLocaleDateString() : '-'}
                        </td>
                        <td className="p-3 text-center text-xs">
                          {row.last_log_date ? new Date(row.last_log_date).toLocaleDateString() : '-'}
                        </td>
                        <td className="p-3 text-center">
                          <button
                            onClick={() => setSelectedBoatHistory(row)}
                            className="text-theme-primary hover:underline text-sm"
                          >
                            Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ==================== METERS REPORT TAB ==================== */}
      {activeTab === 'meters' && (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-theme-primary mb-2">Meters Report (athlete totals)</h3>
            <p className="text-sm text-gray-600 mb-2">
              Totals come from <span className="font-medium">user_meters_log</span>: every credit is
              a single row, so on-water, training, and other credits are not double-counted in the
              total. Coaches see their team; admins and superadmins (with club context) see the full
              club, optionally filtered by team.
            </p>
            <p className="text-xs text-gray-500 mb-4">
              On-water = credit from a boat log; training = from a training log (when not linked to
              boat first); other = direct entries. Export CSV for spreadsheet use.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Start date</label>
                <input
                  type="date"
                  value={metersReportStartDate}
                  onChange={(e) => setMetersReportStartDate(e.target.value)}
                  className="border rounded px-3 py-2 text-sm w-full"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">End date</label>
                <input
                  type="date"
                  value={metersReportEndDate}
                  onChange={(e) => setMetersReportEndDate(e.target.value)}
                  className="border rounded px-3 py-2 text-sm w-full"
                />
              </div>
              {isAdmin() && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Team (optional)</label>
                  <select
                    value={metersTeamFilter}
                    onChange={(e) => setMetersTeamFilter(e.target.value)}
                    className="border rounded px-3 py-2 text-sm w-full"
                  >
                    <option value="">All teams (club)</option>
                    {availableTeams.map((team) => (
                      <option key={team} value={team}>
                        {team}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex items-end lg:col-span-2">
                <button
                  onClick={fetchUnifiedMetersReport}
                  disabled={!metersReportStartDate || !metersReportEndDate || unifiedMetersLoading}
                  className="bg-theme-primary text-white px-4 py-2 rounded text-sm hover:opacity-90 disabled:opacity-50 w-full sm:w-auto"
                >
                  {unifiedMetersLoading ? 'Generating...' : 'Generate report'}
                </button>
              </div>
            </div>
          </div>

          {unifiedMetersReport && (
            <div className="space-y-4">
              {unifiedMetersReport.summary?.data_note && (
                <p className="text-sm text-gray-600 border-l-4 border-teal-500 pl-3 py-1">
                  {unifiedMetersReport.summary.data_note}
                </p>
              )}
              <div className="bg-theme-bg-secondary rounded-lg p-4">
                <h3 className="font-semibold text-theme-primary mb-3">Summary</h3>
                <p className="text-xs text-gray-500 mb-2">
                  Scope:{' '}
                  <span className="font-mono text-gray-700">
                    {unifiedMetersReport.summary?.scope}
                  </span>
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-theme-primary">
                      {unifiedMetersReport.summary?.total_people}
                    </div>
                    <div className="text-xs text-gray-600">People</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-theme-primary">
                      {unifiedMetersReport.summary?.total_logs}
                    </div>
                    <div className="text-xs text-gray-600">Log lines</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-teal-700">
                      {Number(unifiedMetersReport.summary?.total_water_meters || 0).toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-600">On-water (m)</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-700">
                      {Number(
                        unifiedMetersReport.summary?.total_training_meters || 0
                      ).toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-600">Training (m)</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-amber-700">
                      {Number(
                        unifiedMetersReport.summary?.total_other_meters || 0
                      ).toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-600">Other (m)</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-theme-primary">
                      {Number(
                        unifiedMetersReport.summary?.total_meters || 0
                      ).toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-600">Total (m)</div>
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={exportUnifiedMetersCSV}
                        className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 w-full"
                      >
                        Export CSV
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                {unifiedMetersReport.report.length === 0 ? (
                  <p className="text-gray-500 text-sm py-6 text-center">No rows in this range.</p>
                ) : (
                  <table className="w-full bg-white rounded-lg shadow-sm text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-3 font-semibold">Name</th>
                        <th className="text-left p-3 font-semibold">Team</th>
                        <th className="text-right p-3 font-semibold">On-water</th>
                        <th className="text-right p-3 font-semibold">Training</th>
                        <th className="text-right p-3 font-semibold">Other</th>
                        <th className="text-right p-3 font-semibold">Total</th>
                        <th className="text-center p-3 font-semibold">Logs</th>
                        <th className="text-center p-3 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unifiedMetersReport.report.map((row) => (
                        <tr key={row.user_id} className="border-t hover:bg-gray-50">
                          <td className="p-3 font-medium">{row.user_name}</td>
                          <td className="p-3 text-gray-600">{row.user_team || '—'}</td>
                          <td className="p-3 text-right font-mono text-teal-800">
                            {Number(row.water_meters || 0).toLocaleString()}
                          </td>
                          <td className="p-3 text-right font-mono text-blue-800">
                            {Number(row.training_meters || 0).toLocaleString()}
                          </td>
                          <td className="p-3 text-right font-mono text-amber-800">
                            {Number(row.other_meters || 0).toLocaleString()}
                          </td>
                          <td className="p-3 text-right font-mono font-semibold">
                            {Number(row.total_meters || 0).toLocaleString()}
                          </td>
                          <td className="p-3 text-center font-mono">{row.log_count}</td>
                          <td className="p-3 text-center">
                            <button
                              type="button"
                              onClick={() => openUnifiedMetersDetail(row)}
                              className="text-theme-primary hover:underline text-sm"
                            >
                              Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ==================== DAMAGE REPORTS TAB ==================== */}
      {activeTab === 'damage' && (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-theme-primary mb-2">Equipment Damage Reports</h3>
            <p className="text-sm text-gray-600 mb-4">
              View and track all damage reports for club equipment.
            </p>
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Status</label>
                <select
                  value={damageStatusFilter}
                  onChange={(e) => setDamageStatusFilter(e.target.value)}
                  className="border rounded px-3 py-2 text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Severity</label>
                <select
                  value={damageSeverityFilter}
                  onChange={(e) => setDamageSeverityFilter(e.target.value)}
                  className="border rounded px-3 py-2 text-sm"
                >
                  <option value="all">All Severity</option>
                  <option value="minor">Minor</option>
                  <option value="moderate">Moderate</option>
                  <option value="severe">Severe</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <button
                onClick={fetchDamageReports}
                disabled={damageLoading}
                className="bg-theme-primary text-white px-4 py-2 rounded text-sm hover:opacity-90 disabled:opacity-50"
              >
                {damageLoading ? 'Loading...' : 'Refresh'}
              </button>
              {damageReports.length > 0 && (
                <button
                  onClick={exportDamageCSV}
                  className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700"
                >
                  Export CSV
                </button>
              )}
            </div>
          </div>

          {damageLoading ? (
            <div className="text-center py-8 text-gray-500">Loading damage reports...</div>
          ) : damageReports.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No damage reports found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-lg shadow-sm text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-3 font-semibold">Boat</th>
                    <th className="text-center p-3 font-semibold">Severity</th>
                    <th className="text-center p-3 font-semibold">Status</th>
                    <th className="text-left p-3 font-semibold">Description</th>
                    <th className="text-center p-3 font-semibold">Reported</th>
                    <th className="text-center p-3 font-semibold">By</th>
                    <th className="text-center p-3 font-semibold">Assignee</th>
                  </tr>
                </thead>
                <tbody>
                  {damageReports.map((report) => (
                    <tr key={report.id} className="border-t hover:bg-gray-50">
                      <td className="p-3">
                        <div className="font-medium">{report.boat_name}</div>
                        <div className="text-xs text-gray-500">{report.boat_class}</div>
                        {report.location_on_boat && (
                          <div className="text-xs text-gray-400">@ {report.location_on_boat}</div>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getSeverityColor(report.severity)}`}>
                          {report.severity}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(report.status)}`}>
                          {report.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="p-3 max-w-xs">
                        <div className="truncate">{report.description}</div>
                        {report.resolution_notes && (
                          <div className="text-xs text-green-600 mt-1 truncate">
                            ✓ {report.resolution_notes}
                          </div>
                        )}
                      </td>
                      <td className="p-3 text-center text-xs">{report.discovered_date}</td>
                      <td className="p-3 text-center text-xs">{report.reported_by_name}</td>
                      <td className="p-3 text-center text-xs">{report.assignee_name || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Athlete Sign-up History Modal */}
      {selectedAthleteHistory && (
        <MobileModal
          isOpen={true}
          onClose={() => setSelectedAthleteHistory(null)}
          title={`${selectedAthleteHistory.athlete.name}'s Sign-ups`}
        >
          <div className="p-4">
            <div className="mb-4 text-sm">
              <span className="font-medium">{selectedAthleteHistory.total}</span> total sign-ups
              {' • '}
              <span className="text-green-600">{selectedAthleteHistory.past}</span> completed
              {' • '}
              <span className="text-blue-600">{selectedAthleteHistory.upcoming}</span> upcoming
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {selectedAthleteHistory.signups.map((signup) => (
                <div 
                  key={signup.id}
                  className={`border rounded p-3 text-sm ${
                    signup.is_past ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-blue-500'
                  }`}
                >
                  <div className="font-medium">{signup.title}</div>
                  <div className="text-gray-600">
                    {signup.date} • Session {signup.session_number}
                    {signup.team && <span> • {signup.team}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MobileModal>
      )}

      {/* Boat Usage History Modal */}
      {selectedBoatHistory && (
        <MobileModal
          isOpen={true}
          onClose={() => setSelectedBoatHistory(null)}
          title={`${selectedBoatHistory.boat_name} - Meter Logs`}
        >
          <div className="p-4">
            <div className="mb-4">
              <div className="text-sm text-gray-600">{selectedBoatHistory.boat_class}</div>
              <div className="text-sm mt-1">
                <span className="font-medium">{selectedBoatHistory.log_count}</span> logs
                {' • '}
                <span className="text-blue-600">{selectedBoatHistory.unique_loggers}</span> loggers
                {' • '}
                <span className="text-green-600">{Number(selectedBoatHistory.total_meters || 0).toLocaleString()}m</span> total
              </div>
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {selectedBoatHistory.logs?.map((log, idx) => (
                <div 
                  key={idx}
                  className="border rounded p-3 text-sm border-l-4 border-l-theme-primary"
                >
                  <div className="font-medium">{log.logger_name || 'Unknown'}</div>
                  <div className="text-gray-600">
                    {log.date} • {Number(log.meters || 0).toLocaleString()}m
                  </div>
                  {log.notes && <div className="text-gray-500 italic">{log.notes}</div>}
                </div>
              ))}
            </div>
          </div>
        </MobileModal>
      )}

      {unifiedMetersDetail && (
        <MobileModal
          isOpen={true}
          onClose={() => setUnifiedMetersDetail(null)}
          title={`${
            unifiedMetersDetail.user.name || unifiedMetersDetail.user.user_name || 'Athlete'
          } — meters (lines)`}
        >
          <div className="p-4">
            {(unifiedMetersDetail.user.team || unifiedMetersDetail.user.user_team) && (
              <div className="text-sm text-gray-600 mb-2">
                {unifiedMetersDetail.user.team || unifiedMetersDetail.user.user_team}
              </div>
            )}
            {unifiedMetersDetail.error && (
              <p className="text-sm text-red-600 mb-2">{unifiedMetersDetail.error}</p>
            )}
            {unifiedMetersDetail.loading && (
              <p className="text-sm text-gray-500">Loading line items…</p>
            )}
            {!unifiedMetersDetail.loading && !unifiedMetersDetail.error && (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {unifiedMetersDetail.lines?.map((log, idx) => {
                  const source =
                    log.boat_meters_id != null
                      ? 'On-water'
                      : log.training_log_id != null
                        ? 'Training'
                        : 'Other';
                  return (
                    <div
                      key={log.id ?? idx}
                      className="border rounded p-3 text-sm border-l-4 border-l-theme-primary"
                    >
                      <div className="font-medium">{log.modality || '—'}</div>
                      <div className="text-gray-600">
                        {log.log_date} • {Number(log.meters || 0).toLocaleString()}m
                        <span className="ml-1 text-gray-500">({source})</span>
                      </div>
                      {log.notes && (
                        <div className="text-gray-500 italic mt-0.5">{log.notes}</div>
                      )}
                    </div>
                  );
                })}
                {unifiedMetersDetail.lines?.length === 0 && !unifiedMetersDetail.error && (
                  <p className="text-sm text-gray-500">No lines in this range.</p>
                )}
              </div>
            )}
          </div>
        </MobileModal>
      )}
    </div>
  );
}

